import React, { useState, useEffect, useCallback } from 'react';
import { GraduationCap, Loader2, Plus, Trash2, Eye, ArrowRight, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';
import { schoolApi } from '../../api/school';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

const ACTION_LABELS = { promote: 'Promote', graduate: 'Graduate', leave: 'Left' };

export const PromoteStudentsWizard = ({ open, onOpenChange, orgId, years = [], onDone }) => {
  const [fromYear, setFromYear] = useState('');
  const [toYear, setToYear] = useState('');
  const [fromClasses, setFromClasses] = useState([]);
  const [toClasses, setToClasses] = useState([]);
  const [sectionCache, setSectionCache] = useState({}); // key `${yearId}:${classId}` -> sections
  const [rows, setRows] = useState([]);   // {from_class_id, from_section_id, to_class_id, to_section_id}
  const [preview, setPreview] = useState(null); // {mappings:[{...,students:[]}], total_students}
  const [overrides, setOverrides] = useState({}); // student_id -> action
  const [expanded, setExpanded] = useState({});   // row index -> bool
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      const active = years.find((y) => y.is_active);
      const others = years.filter((y) => !y.is_active);
      setFromYear(active?.id || (years[0]?.id || ''));
      setToYear(others[0]?.id || '');
      setRows([]); setPreview(null); setOverrides({}); setResult(null); setExpanded({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadClasses = useCallback(async (yearId, setter) => {
    if (!yearId) { setter([]); return; }
    try { setter(await schoolApi.listClasses(orgId, yearId)); } catch { setter([]); }
  }, [orgId]);

  useEffect(() => { if (open) loadClasses(fromYear, setFromClasses); }, [open, fromYear, loadClasses]);
  useEffect(() => { if (open) loadClasses(toYear, setToClasses); }, [open, toYear, loadClasses]);

  const getSections = useCallback(async (yearId, classId) => {
    const key = `${yearId}:${classId}`;
    if (sectionCache[key]) return sectionCache[key];
    try {
      const secs = await schoolApi.listSections(orgId, classId, yearId);
      setSectionCache((c) => ({ ...c, [key]: secs }));
      return secs;
    } catch { return []; }
  }, [orgId, sectionCache]);

  const addRow = () => setRows((r) => [...r, { from_class_id: '', from_section_id: '', to_class_id: '', to_section_id: '' }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, ...patch } : row));

  const completeRows = rows.filter((r) => r.from_class_id && r.to_class_id);

  const doPreview = async () => {
    if (completeRows.length === 0) { toast.error('Add at least one complete mapping (source + target class)'); return; }
    if (fromYear === toYear) { toast.error('Source and target years must differ'); return; }
    setLoading(true); setPreview(null);
    try {
      const res = await schoolApi.promotionsPreview({ organization_id: orgId, from_year_id: fromYear, to_year_id: toYear, mappings: completeRows });
      setPreview(res);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed'); }
    finally { setLoading(false); }
  };

  const doApply = async () => {
    if (completeRows.length === 0) { toast.error('Nothing to apply'); return; }
    setApplying(true);
    try {
      const ovs = Object.entries(overrides)
        .filter(([, action]) => action && action !== 'promote')
        .map(([student_id, action]) => ({ student_id, action }));
      const res = await schoolApi.promotionsApply({
        organization_id: orgId, from_year_id: fromYear, to_year_id: toYear,
        mappings: completeRows, overrides: ovs,
      });
      setResult(res);
      toast.success('Promotions applied');
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Apply failed'); }
    finally { setApplying(false); }
  };

  const yearName = (id) => years.find((y) => y.id === id)?.name || '—';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="promote-students-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GraduationCap className="w-5 h-5 text-primary" /> Promote Students</DialogTitle>
          <DialogDescription>Manually map each source class/section to a target class/section in the new year. Preview, then apply. Re-running is safe (no duplicate enrollments).</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4" data-testid="promote-result">
            <div className="flex items-center gap-2 text-emerald-600 font-medium"><CheckCircle2 className="w-5 h-5" /> Promotions applied</div>
            <div className="grid grid-cols-4 gap-3">
              {[['created', 'Enrolled'], ['skipped_existing', 'Already existed'], ['graduated', 'Graduated'], ['left', 'Left']].map(([k, label]) => (
                <div key={k} className="rounded-lg border border-border p-3 text-center">
                  <p className="text-2xl font-semibold">{result[k] ?? 0}</p>
                  <p className="text-xs text-muted-foreground">{label}</p>
                </div>
              ))}
            </div>
            {(result.errors || []).length > 0 && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-700 max-h-32 overflow-y-auto">
                {result.errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
            <DialogFooter><Button onClick={() => onOpenChange(false)} data-testid="promote-done">Done</Button></DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Promote from year</Label>
                <Select value={fromYear} onValueChange={(v) => { setFromYear(v); setPreview(null); }}>
                  <SelectTrigger data-testid="from-year-select"><SelectValue placeholder="Source year" /></SelectTrigger>
                  <SelectContent>{years.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}{y.is_active ? ' (active)' : ''}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Into year</Label>
                <Select value={toYear} onValueChange={(v) => { setToYear(v); setPreview(null); }}>
                  <SelectTrigger data-testid="to-year-select"><SelectValue placeholder="Target year" /></SelectTrigger>
                  <SelectContent>{years.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}{y.is_active ? ' (active)' : ''}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Class mappings ({yearName(fromYear)} <ArrowRight className="w-3 h-3 inline" /> {yearName(toYear)})</Label>
                <Button size="sm" variant="outline" onClick={addRow} className="gap-1" data-testid="add-mapping-row"><Plus className="w-3.5 h-3.5" /> Add mapping</Button>
              </div>
              {rows.length === 0 && <p className="text-xs text-muted-foreground">No mappings yet. Click "Add mapping" to map a source class to a target class.</p>}
              {rows.map((row, i) => (
                <MappingRow key={i} idx={i} row={row} fromClasses={fromClasses} toClasses={toClasses}
                  fromYear={fromYear} toYear={toYear} getSections={getSections}
                  onChange={(patch) => { updateRow(i, patch); setPreview(null); }} onRemove={() => removeRow(i)} />
              ))}
            </div>

            {preview && (
              <div className="rounded-lg border border-border p-3 space-y-2" data-testid="promote-preview">
                <p className="text-sm font-medium">{preview.total_students} student(s) will be processed</p>
                {preview.mappings.map((m, i) => (
                  <div key={i} className="border border-border/60 rounded-md">
                    <button className="w-full flex items-center justify-between px-3 py-2 text-sm" onClick={() => setExpanded((e) => ({ ...e, [i]: !e[i] }))}>
                      <span className="flex items-center gap-2">{expanded[i] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />} Mapping {i + 1}</span>
                      <Badge variant="outline">{m.student_count} students</Badge>
                    </button>
                    {expanded[i] && (
                      <div className="px-3 pb-3 space-y-1 max-h-56 overflow-y-auto">
                        {m.students.length === 0 && <p className="text-xs text-muted-foreground">No students enrolled in this source class/section.</p>}
                        {m.students.map((s) => (
                          <div key={s.id} className="flex items-center justify-between gap-2 text-sm border-b border-border/40 py-1">
                            <span className="truncate">{s.name} {s.admission_no ? <span className="text-xs text-muted-foreground">· {s.admission_no}</span> : null}</span>
                            <Select value={overrides[s.id] || 'promote'} onValueChange={(v) => setOverrides((o) => ({ ...o, [s.id]: v }))}>
                              <SelectTrigger className="h-7 w-[120px] text-xs" data-testid={`student-action-${s.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {Object.entries(ACTION_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={applying}>Cancel</Button>
              <Button variant="outline" onClick={doPreview} disabled={loading || applying} className="gap-2" data-testid="promote-preview-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Preview
              </Button>
              <Button onClick={doApply} disabled={applying || !preview} className="gap-2" data-testid="promote-apply-btn">
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <GraduationCap className="w-4 h-4" />} Apply Promotions
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const MappingRow = ({ idx, row, fromClasses, toClasses, fromYear, toYear, getSections, onChange, onRemove }) => {
  const [fromSecs, setFromSecs] = useState([]);
  const [toSecs, setToSecs] = useState([]);

  useEffect(() => { if (row.from_class_id) getSections(fromYear, row.from_class_id).then(setFromSecs); else setFromSecs([]); }, [row.from_class_id, fromYear, getSections]);
  useEffect(() => { if (row.to_class_id) getSections(toYear, row.to_class_id).then(setToSecs); else setToSecs([]); }, [row.to_class_id, toYear, getSections]);

  return (
    <div className="grid grid-cols-[1fr_auto_1fr_auto] items-end gap-2 border border-border rounded-md p-2" data-testid={`mapping-row-${idx}`}>
      <div className="grid grid-cols-2 gap-1">
        <Select value={row.from_class_id} onValueChange={(v) => onChange({ from_class_id: v, from_section_id: '' })}>
          <SelectTrigger className="h-8 text-xs" data-testid={`from-class-${idx}`}><SelectValue placeholder="From class" /></SelectTrigger>
          <SelectContent>{fromClasses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={row.from_section_id || 'ALL'} onValueChange={(v) => onChange({ from_section_id: v === 'ALL' ? '' : v })} disabled={!row.from_class_id}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="All sections" /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">All sections</SelectItem>{fromSecs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <ArrowRight className="w-4 h-4 text-muted-foreground mb-2" />
      <div className="grid grid-cols-2 gap-1">
        <Select value={row.to_class_id} onValueChange={(v) => onChange({ to_class_id: v, to_section_id: '' })}>
          <SelectTrigger className="h-8 text-xs" data-testid={`to-class-${idx}`}><SelectValue placeholder="To class" /></SelectTrigger>
          <SelectContent>{toClasses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={row.to_section_id || 'ALL'} onValueChange={(v) => onChange({ to_section_id: v === 'ALL' ? '' : v })} disabled={!row.to_class_id}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Section" /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">No section</SelectItem>{toSecs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={onRemove}><Trash2 className="w-4 h-4" /></Button>
    </div>
  );
};
